import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import type { KeyValueStorageInterface } from 'aws-amplify/utils';
import { defaultStorage } from 'aws-amplify/utils';

import { CLARIUM_DOMAIN_SESSION_AUTH_STORAGE_KEY } from 'utils/auth';
import { getAnonymousBootstrapFlag } from 'utils/launchDarklyClient';

export const LOGOUT_ON_BROWSER_CLOSE_FLAG = 'hasLogoutOnBrowserClose';

export const SESSION_STORAGE_CLIENT_DOMAINS: string[] = (import.meta.env.VITE_SESSION_STORAGE_CLIENT_DOMAINS ?? '')
  .split(',')
  .map((d: string) => d.trim().toLowerCase())
  .filter((d: string) => d !== '' && d !== null && d !== undefined);

export async function shouldUserUseSessionStorage(email: string) {
  const emailLower = email.toLowerCase();
  const emailDomain = emailLower.split('@')[1]?.trim();
  const hasLogoutOnBrowserClose = await getAnonymousBootstrapFlag(LOGOUT_ON_BROWSER_CLOSE_FLAG);
  if (hasLogoutOnBrowserClose && SESSION_STORAGE_CLIENT_DOMAINS.includes(emailDomain)) {
    return true;
  }
  return false;
}

const baseUrls = [
  'http://localhost:3000',
  'https://app-dev.clariumhealth.com',
  'https://app-staging.clariumhealth.com',
  'https://app-qa.clariumhealth.com',
  'https://app.clariumhealth.com',
];

const redirectSignInUrls = baseUrls.map((url) => `${url}/login/callback`);
/** Must match allowed sign-out URLs on the Cognito app client so hosted UI `/logout` can redirect back. */
const redirectSignOutUrls = baseUrls.map((url) => `${url}/login`);

Amplify.configure({
  Auth: {
    Cognito: {
      identityPoolId: process.env.VITE_AWS_COGNITO_IDENTITY_POOL!,
      userPoolId: process.env.VITE_COGNITO_USER_POOL_ID!,
      userPoolClientId: process.env.VITE_COGNITO_USER_POOL_WEB_CLIENT_ID!,
      allowGuestAccess: true,
      loginWith: {
        email: true,
        oauth: {
          domain: process.env.VITE_COGNITO_DOMAIN!,
          scopes: ['email', 'openid', 'profile'],
          redirectSignIn: redirectSignInUrls,
          redirectSignOut: redirectSignOutUrls,
          responseType: 'code',
        },
      },
    },
  },
  Storage: {
    S3: {
      bucket: `${process.env.VITE_AWS_BUCKET}`, //REQUIRED -  Amazon S3 bucket name
      region: process.env.VITE_AWS_REGION, //OPTIONAL -  Amazon service region
    },
  },
});

const hybridStorage: KeyValueStorageInterface = {
  async getItem(key: string) {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem(key);
  },
  async setItem(key: string, value: string) {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(key, value);
  },
  async removeItem(key: string) {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(key);
  },
  async clear() {
    if (typeof window === 'undefined') return;
    window.sessionStorage.clear();
  },
};

const hasLogoutOnBrowserClose = await getAnonymousBootstrapFlag(LOGOUT_ON_BROWSER_CLOSE_FLAG);
const clariumDomainSessionAuth =
  typeof window !== 'undefined' && window.sessionStorage.getItem(CLARIUM_DOMAIN_SESSION_AUTH_STORAGE_KEY) === '1';
export const useHybridTokenStorage = hasLogoutOnBrowserClose && clariumDomainSessionAuth;

cognitoUserPoolsTokenProvider.setKeyValueStorage(useHybridTokenStorage ? hybridStorage : defaultStorage);

export { hybridStorage };
